import React, { useEffect, useState } from 'react';
import { Card, Table, Button, Combobox, DateInput, Badge } from '../../../components/common';
import { apiError } from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import { bankReportService, bankAccountService } from '../../../services/resources';
import { sortByLabel } from '../../../config/columns';

const fmt = (n) =>
  n === null || n === undefined || n === '' ? '' : Number(n).toLocaleString(undefined, { maximumFractionDigits: 4 });

const today = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => today().slice(0, 8) + '01';

export default function BankReportPage() {
  const { can } = useAuth();
  const canView = can('REPORT_BANK', 'CanView');

  const [dateFrom, setDateFrom] = useState(firstOfMonth());
  const [dateTo, setDateTo] = useState(today());
  const [bankId, setBankId] = useState('');
  const [banks, setBanks] = useState([]);

  const [allAsOf, setAllAsOf] = useState(today());

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);

  const [allBalances, setAllBalances] = useState(null);
  const [allLoading, setAllLoading] = useState(false);
  const [allError, setAllError] = useState('');
  const [allExporting, setAllExporting] = useState(false);

  useEffect(() => {
    bankAccountService.list().then((rows) => {
      setBanks(rows);
      if (rows.length > 0 && !bankId) setBankId(rows[0].id);
    }).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const filters = { dateFrom, dateTo, bank_id: bankId };

  const load = async () => {
    if (!bankId) return;
    setLoading(true);
    setError('');
    try {
      setData(await bankReportService.list(filters));
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const loadAllBalances = async () => {
    setAllLoading(true);
    setAllError('');
    try {
      setAllBalances(await bankReportService.allBalances({ dateTo: allAsOf }));
    } catch (err) {
      setAllError(apiError(err));
    } finally {
      setAllLoading(false);
    }
  };

  useEffect(() => {
    if (canView && bankId) load();
    // eslint-disable-next-line
  }, [bankId]);

  const doExport = async () => {
    setExporting(true);
    try {
      await bankReportService.export(filters);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setExporting(false);
    }
  };

  const doExportAll = async () => {
    setAllExporting(true);
    try {
      await bankReportService.exportAllBalances({ dateTo: allAsOf });
    } catch (err) {
      setAllError(apiError(err));
    } finally {
      setAllExporting(false);
    }
  };

  if (!canView) {
    return (
      <Card title="Bank Report">
        <p className="text-sm text-gray-500">You do not have permission to view this page.</p>
      </Card>
    );
  }

  const columns = [
    { key: 'date', label: 'Date' },
    { key: 'doc_no', label: 'Doc No' },
    { key: 'description', label: 'Description' },
    { key: 'debit', label: 'Debit', render: (r) => (r.debit ? fmt(r.debit) : ''), align: 'right' },
    { key: 'kredit', label: 'Kredit', render: (r) => (r.kredit ? fmt(r.kredit) : ''), align: 'right' },
    { key: 'running_balance', label: 'Running Balance', render: (r) => fmt(r.running_balance), align: 'right' },
  ];

  const allBalancesColumns = [
    { key: 'bank_name', label: 'Bank' },
    { key: 'currency_code', label: 'Currency' },
    {
      key: 'is_blocked',
      label: 'Status',
      render: (r) => (r.is_blocked ? <Badge tone="red">Blocked</Badge> : <Badge tone="green">Active</Badge>),
    },
    { key: 'balance', label: 'Balance', render: (r) => fmt(r.balance), align: 'right' },
  ];

  return (
    <div className="space-y-4">
      <Card title="Bank Report">
        <div className="flex flex-wrap items-end gap-3">
          <DateInput label="From" value={dateFrom} onChange={setDateFrom} />
          <DateInput label="To" value={dateTo} onChange={setDateTo} />
          <div className="w-56">
            <Combobox
              label="Bank"
              value={bankId}
              onChange={setBankId}
              required
              options={sortByLabel(banks.map((b) => ({ value: b.id, label: b.bank_name })))}
            />
          </div>
          <Button onClick={load} disabled={loading || !bankId}>
            {loading ? 'Loading…' : 'Apply'}
          </Button>
          <Button variant="secondary" onClick={doExport} disabled={exporting || !bankId}>
            {exporting ? 'Exporting…' : 'Export to Excel'}
          </Button>
        </div>
        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      </Card>

      {data && (
        <Card>
          <div className="mb-3 flex flex-wrap gap-6 text-sm text-gray-600">
            <span>Opening Balance: <strong>{fmt(data.openingBalance)}</strong></span>
            <span>Closing Balance: <strong>{fmt(data.closingBalance)}</strong></span>
            <span>Total Debit: <strong>{fmt(data.totalDebit)}</strong></span>
            <span>Total Kredit: <strong>{fmt(data.totalKredit)}</strong></span>
          </div>
          <Table columns={columns} rows={data.rows} loading={loading} maxHeight="60vh" />
        </Card>
      )}

      <Card title="All Banks">
        <div className="flex flex-wrap items-end gap-3">
          <DateInput label="As Of" value={allAsOf} onChange={setAllAsOf} />
          <Button onClick={loadAllBalances} disabled={allLoading}>
            {allLoading ? 'Loading…' : allBalances ? 'Refresh' : 'View All Banks'}
          </Button>
          {allBalances && (
            <Button variant="secondary" onClick={doExportAll} disabled={allExporting}>
              {allExporting ? 'Exporting…' : 'Export to Excel'}
            </Button>
          )}
        </div>
        {allError && <p className="mt-3 text-sm text-red-600">{allError}</p>}
        {allBalances && (
          <div className="mt-4">
            <p className="mb-2 text-xs text-gray-500">Balance as of {allAsOf}</p>
            <Table columns={allBalancesColumns} rows={allBalances} loading={allLoading} />
          </div>
        )}
      </Card>
    </div>
  );
}
